import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { MobileCaptureUploadError, uploadMobileCaptureMedia } from '../mobileCaptureApi'

export type MobileUploadSource = 'camera' | 'gallery'
/** Active queue statuses (not yet in session Uploaded History). */
export type MobileUploadStatus = 'pending' | 'queued' | 'uploading' | 'failed' | 'auth_required'

export interface MobileUploadItem {
  id: string
  file: File
  previewUrl: string
  source: MobileUploadSource
  status: MobileUploadStatus
  progress: number
  retryCount: number
  error?: string
  createdAt: number
}

/** Lightweight session-only success record — no File / original Blob. */
export interface MobileUploadedHistoryItem {
  id: string
  assetId: string
  /** Small preview (downscaled local blob URL, or server URL if usable). */
  thumbnailUrl: string
  serverThumbnailUrl?: string | null
  uploadedAt: number
  source: MobileUploadSource
}

/** Cap parallel mobile uploads — phones + proxy upload path stay responsive. */
export const MAX_CONCURRENT_UPLOADS = 2
const MAX_AUTO_RETRIES = 3
const BACKOFF_MS = [1000, 3000, 8000]
const HISTORY_THUMB_MAX = 240

function newId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) return crypto.randomUUID()
  return `id-${Date.now()}-${Math.random().toString(16).slice(2)}`
}

function isTransientError(err: unknown): boolean {
  if (err instanceof MobileCaptureUploadError) {
    if (err.status === 401 || err.status === 403) return false
    if (err.status !== undefined && err.status >= 500) return true
    if (err.status === 408 || err.status === 429) return true
    return err.status === undefined
  }
  return true
}

function isAuthError(err: unknown): boolean {
  return err instanceof MobileCaptureUploadError && (err.status === 401 || err.status === 403)
}

function revokeUrl(url: string | undefined | null) {
  if (url && url.startsWith('blob:')) URL.revokeObjectURL(url)
}

/**
 * Build a small JPEG blob URL for Uploaded History so we can drop the
 * original File / full-size object URL without waiting on server media auth.
 */
export async function buildHistoryThumbnailUrl(previewUrl: string): Promise<string> {
  try {
    const img = await new Promise<HTMLImageElement>((resolve, reject) => {
      const el = new Image()
      el.onload = () => resolve(el)
      el.onerror = () => reject(new Error('thumb decode failed'))
      el.src = previewUrl
    })
    const scale = Math.min(1, HISTORY_THUMB_MAX / Math.max(img.width, img.height, 1))
    const w = Math.max(1, Math.round(img.width * scale))
    const h = Math.max(1, Math.round(img.height * scale))
    const canvas = document.createElement('canvas')
    canvas.width = w
    canvas.height = h
    const ctx = canvas.getContext('2d')
    if (!ctx) return previewUrl
    ctx.drawImage(img, 0, 0, w, h)
    const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/jpeg', 0.82))
    if (!blob) return previewUrl
    return URL.createObjectURL(blob)
  } catch {
    return previewUrl
  }
}

export interface UseMobileUploadQueueOptions {
  getAccessToken: () => string | null
  /** When true, accept video files (video_asset_library mobile capture). */
  acceptVideos?: boolean
}

function isVideoFile(file: File): boolean {
  if (file.type.startsWith('video/')) return true
  const lower = file.name.toLowerCase()
  return lower.endsWith('.mp4') || lower.endsWith('.mov') || lower.endsWith('.webm')
}

function isImageFile(file: File): boolean {
  return file.type.startsWith('image/') || !file.type
}

/**
 * Three-stage Mobile Capture queue:
 * Capture → Pending → (user confirms) → Uploading → Uploaded History (session).
 * Capture never waits on upload; concurrency is capped.
 */
export function useMobileUploadQueue({ getAccessToken, acceptVideos = false }: UseMobileUploadQueueOptions) {
  const [items, setItems] = useState<MobileUploadItem[]>([])
  const [uploadedHistory, setUploadedHistory] = useState<MobileUploadedHistoryItem[]>([])
  const [authExpired, setAuthExpired] = useState(false)
  const itemsRef = useRef<MobileUploadItem[]>([])
  const historyRef = useRef<MobileUploadedHistoryItem[]>([])
  const authExpiredRef = useRef(false)

  const syncItems = useCallback((updater: (prev: MobileUploadItem[]) => MobileUploadItem[]) => {
    setItems((prev) => {
      const next = updater(prev)
      itemsRef.current = next
      return next
    })
  }, [])

  const syncHistory = useCallback(
    (updater: (prev: MobileUploadedHistoryItem[]) => MobileUploadedHistoryItem[]) => {
      setUploadedHistory((prev) => {
        const next = updater(prev)
        historyRef.current = next
        return next
      })
    },
    [],
  )

  useEffect(() => {
    authExpiredRef.current = authExpired
  }, [authExpired])

  const removePendingItem = useCallback(
    (id: string) => {
      syncItems((prev) => {
        const target = prev.find((it) => it.id === id)
        if (!target || target.status !== 'pending') return prev
        revokeUrl(target.previewUrl)
        return prev.filter((it) => it.id !== id)
      })
    },
    [syncItems],
  )

  const clearPending = useCallback(() => {
    syncItems((prev) => {
      prev.filter((it) => it.status === 'pending').forEach((it) => revokeUrl(it.previewUrl))
      return prev.filter((it) => it.status !== 'pending')
    })
  }, [syncItems])

  const promoteToHistory = useCallback(
    async (item: MobileUploadItem, assetId: string, serverThumbnailUrl?: string | null) => {
      // Free the upload concurrency slot immediately (canonical upload already OK).
      syncItems((prev) => prev.filter((it) => it.id !== item.id))
      const smallUrl = isVideoFile(item.file)
        ? item.previewUrl
        : await buildHistoryThumbnailUrl(item.previewUrl)
      if (item.previewUrl !== smallUrl) {
        revokeUrl(item.previewUrl)
      }
      const historyItem: MobileUploadedHistoryItem = {
        id: item.id,
        assetId,
        thumbnailUrl: smallUrl,
        serverThumbnailUrl: serverThumbnailUrl || null,
        uploadedAt: Date.now(),
        source: item.source,
      }
      // Newest successful upload first.
      syncHistory((prev) => [historyItem, ...prev])
    },
    [syncHistory, syncItems],
  )

  const pump = useCallback(() => {
    if (authExpiredRef.current) return
    const token = getAccessToken()
    if (!token) return

    const uploadingCount = itemsRef.current.filter((it) => it.status === 'uploading').length
    let slots = MAX_CONCURRENT_UPLOADS - uploadingCount
    if (slots <= 0) return

    const toStart: MobileUploadItem[] = []
    for (const it of itemsRef.current) {
      if (slots <= 0) break
      if (it.status === 'queued') {
        toStart.push(it)
        slots -= 1
      }
    }
    if (toStart.length === 0) return

    const startIds = new Set(toStart.map((it) => it.id))
    syncItems((prev) =>
      prev.map((it) =>
        startIds.has(it.id) ? { ...it, status: 'uploading', progress: 0, error: undefined } : it,
      ),
    )

    for (const claimed of toStart) {
      void (async () => {
        const latest = itemsRef.current.find((it) => it.id === claimed.id) || claimed
        try {
          const result = await uploadMobileCaptureMedia(token, latest.file, {
            attemptId: claimed.id,
            onProgress: (pct) => {
              syncItems((prev) =>
                prev.map((it) => (it.id === claimed.id ? { ...it, progress: pct } : it)),
              )
            },
          })
          // Canonical ingestion succeeded — enter Uploaded History immediately
          // (do not wait for transparent / background / shadow).
          const snap = itemsRef.current.find((it) => it.id === claimed.id) || latest
          await promoteToHistory(snap, result.id, result.thumbnail_url)
        } catch (err) {
          if (isAuthError(err)) {
            authExpiredRef.current = true
            setAuthExpired(true)
            syncItems((prev) =>
              prev.map((it) =>
                it.id === claimed.id
                  ? {
                      ...it,
                      status: 'auth_required',
                      error: '连接已失效，请重新登录',
                      progress: 0,
                    }
                  : it.status === 'queued'
                    ? {
                        ...it,
                        status: 'auth_required',
                        error: '连接已失效，请重新登录',
                      }
                    : it,
              ),
            )
            return
          }

          const retryCount = latest.retryCount + 1
          if (isTransientError(err) && retryCount <= MAX_AUTO_RETRIES) {
            const delay = BACKOFF_MS[Math.min(retryCount - 1, BACKOFF_MS.length - 1)]
            syncItems((prev) =>
              prev.map((it) =>
                it.id === claimed.id
                  ? {
                      ...it,
                      status: 'queued',
                      retryCount,
                      progress: 0,
                      error: `自动重试中（${retryCount}/${MAX_AUTO_RETRIES}）…`,
                    }
                  : it,
              ),
            )
            window.setTimeout(() => pump(), delay)
          } else {
            syncItems((prev) =>
              prev.map((it) =>
                it.id === claimed.id
                  ? {
                      ...it,
                      status: 'failed',
                      retryCount,
                      progress: 0,
                      error: err instanceof Error ? err.message : '上传失败',
                    }
                  : it,
              ),
            )
          }
        } finally {
          pump()
        }
      })()
    }
  }, [getAccessToken, promoteToHistory, syncItems])

  useEffect(() => {
    pump()
  }, [items, pump])

  const enqueueFiles = useCallback(
    (list: FileList | File[] | null, source: MobileUploadSource) => {
      if (!list || authExpiredRef.current) return 0
      const files = Array.from(list).filter((f) =>
        acceptVideos ? isVideoFile(f) : isImageFile(f),
      )
      if (files.length === 0) return 0
      const added: MobileUploadItem[] = files.map((file) => ({
        id: newId(),
        file,
        previewUrl: URL.createObjectURL(file),
        source,
        status: 'pending',
        progress: 0,
        retryCount: 0,
        createdAt: Date.now(),
      }))
      // Newest capture first in Pending.
      syncItems((prev) => [...added, ...prev])
      return added.length
    },
    [syncItems],
  )

  /** Pending → queued (upload worker). Capture stays free. */
  const startUpload = useCallback(() => {
    if (authExpiredRef.current) return 0
    let n = 0
    syncItems((prev) =>
      prev.map((it) => {
        if (it.status !== 'pending') return it
        n += 1
        return { ...it, status: 'queued' as const, progress: 0, error: undefined }
      }),
    )
    return n
  }, [syncItems])

  const retryItem = useCallback(
    (id: string) => {
      if (authExpiredRef.current) return
      syncItems((prev) =>
        prev.map((it) =>
          it.id === id && (it.status === 'failed' || it.status === 'auth_required')
            ? { ...it, status: 'queued', progress: 0, error: undefined, retryCount: 0 }
            : it,
        ),
      )
    },
    [syncItems],
  )

  useEffect(() => {
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      const active = itemsRef.current.some(
        (it) => it.status === 'pending' || it.status === 'queued' || it.status === 'uploading',
      )
      if (!active) return
      e.preventDefault()
      e.returnValue = ''
    }
    window.addEventListener('beforeunload', onBeforeUnload)
    return () => window.removeEventListener('beforeunload', onBeforeUnload)
  }, [])

  useEffect(() => {
    return () => {
      itemsRef.current.forEach((it) => revokeUrl(it.previewUrl))
      historyRef.current.forEach((it) => revokeUrl(it.thumbnailUrl))
    }
  }, [])

  const pendingItems = useMemo(() => items.filter((it) => it.status === 'pending'), [items])
  const uploadingItems = useMemo(
    () =>
      items.filter(
        (it) =>
          it.status === 'queued' ||
          it.status === 'uploading' ||
          it.status === 'failed' ||
          it.status === 'auth_required',
      ),
    [items],
  )

  const counts = {
    pending: pendingItems.length,
    uploading: items.filter((it) => it.status === 'uploading').length,
    queued: items.filter((it) => it.status === 'queued').length,
    inFlight: uploadingItems.length,
    uploaded: uploadedHistory.length,
    failed: items.filter((it) => it.status === 'failed' || it.status === 'auth_required').length,
  }

  const hasActiveUploads = counts.uploading + counts.queued > 0

  return {
    pendingItems,
    uploadingItems,
    uploadedHistory,
    counts,
    hasActiveUploads,
    authExpired,
    maxConcurrent: MAX_CONCURRENT_UPLOADS,
    enqueueFiles,
    startUpload,
    retryItem,
    removePendingItem,
    clearPending,
  }
}
